// Route preloading (ported from Summit Engineering's lib\preload.ts, D-0189): every page is a lazily loaded chunk;
// hovering or focusing its navigation link starts the download so the click feels instant. App.tsx registers each page's
// loader against its path. Release 1.5.0 adds data prefetch on hover (behind the 1.5.0 switch, see registerDataPrefetch).

type Loader = () => Promise<unknown>;

const loaders = new Map<string, Loader[]>();
const started = new Set<string>();

/** Paths whose loader runs for `path`: the exact path, a parent of it, or a child of it. */
function matches(prefix: string, path: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`) || prefix.startsWith(`${path}/`);
}

/** Register a page chunk under its path. A route pattern (`/purchasing/requests/:key`) registers its static prefix. */
export function registerPreload(pathPrefix: string, loader: Loader): void {
  const prefix = pathPrefix.split("/:")[0] || "/";
  const list = loaders.get(prefix) ?? [];
  if (!list.includes(loader)) list.push(loader);
  loaders.set(prefix, list);
}

/**
 * Start loading every page whose registered prefix matches `path` (the page, its parent hub and its tabs — SE's rule).
 * `exact` loads only the one page the URL opens (used at start-up, so nothing competes with the landing page).
 * Safe to call often: each chunk is requested once.
 */
export function preloadRoute(path: string, options: { exact?: boolean } = {}): void {
  const clean = path.split(/[?#]/)[0] ?? path;
  const hits = options.exact
    ? [...loaders.keys()]
        .filter((prefix) => clean === prefix || clean.startsWith(`${prefix}/`))
        .sort((a, b) => b.length - a.length)
        .slice(0, 1)
    : [...loaders.keys()].filter((prefix) => matches(prefix, clean));
  for (const prefix of hits) {
    if (started.has(prefix)) continue;
    started.add(prefix);
    void Promise.all((loaders.get(prefix) ?? []).map((load) => load())).catch(() => started.delete(prefix));
  }
  for (const [prefix, prefetch] of dataPrefetchers) {
    if (prefix === clean) void Promise.resolve(prefetch()).catch(() => undefined);
  }
}

// ── Data prefetch (release 1.5.0, only when the switch is on — main.tsx installs it) ─────────────────────────────────

type DataPrefetch = () => Promise<unknown> | void;
const dataPrefetchers = new Map<string, DataPrefetch>();

/** Warm a page's main query when its link is hovered or focused. Uses the page's own query key, so a hit is a cache hit. */
export function registerDataPrefetch(path: string, prefetch: DataPrefetch): void {
  dataPrefetchers.set(path, prefetch);
}

export function clearDataPrefetchers(): void {
  dataPrefetchers.clear();
}

/** Same-origin application path of a link, or null (external, download, new tab, API). */
export function internalPath(anchor: HTMLAnchorElement, origin: string = window.location.origin): string | null {
  if (anchor.target && anchor.target !== "_self") return null;
  if (anchor.hasAttribute("download")) return null;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return null;
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (url.pathname.startsWith("/api/") || url.pathname.startsWith("/health")) return null;
  return url.pathname;
}

/**
 * One listener for every link in the app (hub tabs, the command palette, drill-down links in tables): hovering or
 * focusing a link preloads its page, exactly as the sidebar does. Returns the uninstall function.
 */
export function installLinkPreload(doc: Document = document): () => void {
  const onIntent = (event: Event) => {
    const target = event.target as Element | null;
    const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!anchor) return;
    const path = internalPath(anchor, doc.defaultView?.location.origin ?? window.location.origin);
    if (path) preloadRoute(path);
  };
  doc.addEventListener("pointerover", onIntent, { passive: true });
  doc.addEventListener("focusin", onIntent);
  return () => {
    doc.removeEventListener("pointerover", onIntent);
    doc.removeEventListener("focusin", onIntent);
  };
}

/** Test helper: forget every registration. */
export function resetPreloadsForTests(): void {
  loaders.clear();
  started.clear();
  dataPrefetchers.clear();
}
